/**
 * taste-animations.js
 * Motion cinématique — Taste Skill × Shopify
 * Intersection Observer + magnetic + parallax
 * Vanilla JS, zéro dépendance
 */

(function () {
  'use strict';

  const prefersReducedMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ─── Intersection Observer (reveal au scroll) ─────────────────────────────

  function initReveal() {
    const single   = document.querySelectorAll(
      '[data-taste="reveal"], [data-taste="reveal-left"], [data-taste="scale-in"], [data-taste="fade"]'
    );
    const stagger  = document.querySelectorAll('[data-taste="stagger"] > *');
    const all      = [...single, ...stagger];

    if (!all.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -48px 0px' }
    );

    all.forEach((el) => observer.observe(el));
  }

  // ─── Parallax (scroll) ───────────────────────────────────────────────────

  function initParallax() {
    if (prefersReducedMotion) return;

    const els = document.querySelectorAll('[data-parallax]');
    if (!els.length) return;

    const onScroll = () => {
      els.forEach((el) => {
        const rect   = el.getBoundingClientRect();
        const speed  = parseFloat(el.dataset.parallax) || 0.15;
        const center = rect.top + rect.height / 2 - window.innerHeight / 2;
        el.style.transform = `translateY(${center * speed}px)`;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // appel initial
  }

  // ─── Magnetic buttons ────────────────────────────────────────────────────

  function initMagnetic() {
    if (prefersReducedMotion) return;

    document.querySelectorAll('[data-magnetic]').forEach((el) => {
      const strength = parseFloat(el.dataset.magnetic) || 0.3;

      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const x    = (e.clientX - rect.left - rect.width  / 2) * strength;
        const y    = (e.clientY - rect.top  - rect.height / 2) * strength;
        el.style.transform  = `translate(${x}px, ${y}px)`;
        el.style.transition = 'transform 0.1s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      });

      el.addEventListener('mouseleave', () => {
        el.style.transform  = 'translate(0, 0)';
        el.style.transition = 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)';
      });
    });
  }

  // ─── Image zoom hover (option via settings) ───────────────────────────────

  function initImageZoom() {
    // Géré en CSS via .product-card:hover .media-wrapper img
    // Ce bloc permet de désactiver si settings.enable_image_zoom = false
    // La désactivation se fait via une classe body ajoutée par Liquid :
    // {% unless settings.enable_image_zoom %}
    //   <style>.media-wrapper img { transition: none !important; }</style>
    // {% endunless %}
  }

  // ─── Vidéos réactives au scroll (playback rate + fade) ──────────────────
  //
  // Vise les <video data-scroll-speed="0.3">.
  // - La vitesse de lecture ralentit avec la vélocité de scroll (jusqu'au min
  //   défini), puis revient doucement à 1 quand le scroll s'arrête.
  // - L'opacité fond vers 0 sur les derniers 30 % de viewport quand l'élément
  //   sort par le haut, pour adoucir la transition vers la section suivante.
  // - Désactivé sous prefers-reduced-motion : la vidéo joue normalement.

  function initScrollVideos() {
    const videos = document.querySelectorAll('video[data-scroll-speed]');
    if (!videos.length) return;

    // Sur mobile / tactile, muter playbackRate + opacity à chaque frame fait
    // saccader le décodeur vidéo. On désactive la réactivité : lecture normale.
    const isTouchOrSmall =
      window.matchMedia('(max-width: 768px)').matches ||
      window.matchMedia('(hover: none)').matches;

    if (prefersReducedMotion || isTouchOrSmall) {
      // Pas de réactivité : on s'assure juste que la vidéo lit normalement.
      videos.forEach((v) => { v.playbackRate = 1; v.style.opacity = ''; });
      return;
    }

    const FADE_REGION = 0.30;   // 30 % du viewport
    const VELOCITY_NORM = 60;    // px de scroll/frame qui sature l'effet
    const LERP = 0.08;           // douceur du retour (0–1, plus petit = plus lent)

    const state = new Map();
    let lastScrollY = window.scrollY;
    let scrollDelta = 0;
    let lastScrollTime = performance.now();

    window.addEventListener('scroll', () => {
      const now = performance.now();
      const dt = Math.max(1, now - lastScrollTime);
      const dy = Math.abs(window.scrollY - lastScrollY);
      // Normalisation par 16ms pour rester stable quelle que soit la fréquence
      scrollDelta = (dy / dt) * 16;
      lastScrollY = window.scrollY;
      lastScrollTime = now;
    }, { passive: true });

    videos.forEach((v) => {
      const min = Math.max(0.1, Math.min(1, parseFloat(v.dataset.scrollSpeed) || 0.3));
      state.set(v, { current: 1, target: 1, min });
      v.playbackRate = 1;
    });

    function tick() {
      // 1) Décay du scrollDelta : sans nouvel évènement, il tombe à 0.
      scrollDelta *= 0.85;

      // 2) Vélocité normalisée [0..1]
      const velocity = Math.min(1, scrollDelta / VELOCITY_NORM);

      const vh = window.innerHeight;

      videos.forEach((v) => {
        const s = state.get(v);

        // Target playbackRate : 1 (immobile) → min (vitesse max de scroll)
        s.target = 1 - velocity * (1 - s.min);

        // Lerp doux vers target
        s.current += (s.target - s.current) * LERP;
        // Petit clamp pour éviter les valeurs négatives ou > 1
        if (s.current < s.min - 0.01) s.current = s.min;
        if (s.current > 1) s.current = 1;

        try { v.playbackRate = s.current; } catch (_) { /* certains formats refusent */ }

        // Fade quand l'élément sort par le haut du viewport
        const rect = v.getBoundingClientRect();
        const fadePx = vh * FADE_REGION;
        let opacity = 1;

        if (rect.bottom < fadePx) {
          opacity = Math.max(0, rect.bottom / fadePx);
        }

        v.style.opacity = opacity.toFixed(3);
      });

      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  // ─── Header scroll (sticky + hide/show) ──────────────────────────────────

  function initHeaderScroll() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    let lastY    = 0;
    let ticking  = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentY = window.scrollY;
          header.classList.toggle('header--scrolled', currentY > 50);
          header.classList.toggle('header--hidden',   currentY > lastY && currentY > 200);
          lastY   = currentY;
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    // initScrollVideos gère lui-même prefers-reduced-motion (pour rester actif
    // en mode "play normal sans réactivité"), donc on l'appelle toujours.
    initScrollVideos();

    if (prefersReducedMotion) return;
    initReveal();
    initParallax();
    initMagnetic();
    initHeaderScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
